import { ArrowRight, Check, Info, MoreVertical } from 'lucide-react';
import { type ReactNode } from 'react';

import {
  Button,
  Badge,
  Card,
  CardContent,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useIsMobile,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from '@design-system';
import { AM_BEST_CARRIER_RATINGS, AM_BEST_RATING_INFO } from '@client/util/amBestRatingData';
import {
  getProductDetailsSections,
  getTraditionalLTCDetailsSections,
  type ProductDetailSection,
} from '@client/util/getProductDetailsSections';
import { formatCurrency } from '@shared/formatCurrency';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';

import {
  type GuidedPlanningQuote,
  getCarrierName,
  getPremiumAmount,
  getPaymentOption,
  getProjectedCoverage,
  getPartnerProjectedCoverage,
  getFormattedROI,
  isTraditionalLTCQuote,
} from '@/features/guided-financial-planning/hooks';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PRODUCTS } from '@client/lib/nyc-lif-demo';
import { UnderwritingStatus } from '@client/features/underwriting-eligibility';

function getPaymentOptionTooltip(
  paymentOption: string | undefined,
  premiumAmount: number,
): string | undefined {
  if (!paymentOption) return undefined;

  const formatted = formatCurrency(premiumAmount);
  const normalized = paymentOption.toLowerCase();

  if (normalized.includes('single') || normalized.includes('premium cash')) {
    return `You pay ${formatted} once and you're covered.`;
  }
  if (normalized.includes('10') || normalized.includes('ten') || normalized.includes('recurring')) {
    return `You pay ${formatted} per year for 10 years.`;
  }
  if (normalized.includes('monthly')) {
    return `You pay ${formatted} every month for the life of the policy.`;
  }
  if (normalized.includes('annual')) {
    return `You pay ${formatted} once a year for the life of the policy.`;
  }
  return undefined;
}

function getDetailsSections(quote: GuidedPlanningQuote): ProductDetailSection[] {
  if (isTraditionalLTCQuote(quote)) {
    return getTraditionalLTCDetailsSections(quote);
  }
  return getProductDetailsSections(quote);
}

function ProductDetailsHoverContent({ sections }: { sections: ProductDetailSection[] }) {
  return (
    <div className="space-y-3">
      {sections.map((section, index) => (
        <div key={index}>
          <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-950">
            {section.title}
          </h4>
          <Separator className="mb-2" />
          <Table>
            <TableBody>
              {section.items.map((item, i) => (
                <TableRow
                  key={i}
                  className="border-0 hover:bg-transparent"
                >
                  {item.label ? (
                    <>
                      <TableCell className="py-1 pl-0 pr-4 text-xs font-medium text-gray-700">
                        {item.label}
                      </TableCell>
                      <TableCell className="py-1 pr-0 text-right text-xs font-semibold text-gray-950">
                        {item.value}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell
                      colSpan={2}
                      className="py-1 pl-0 pr-0 text-xs font-semibold text-gray-950"
                    >
                      {item.value}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

interface ProductDetailsHoverCardProps {
  quote: GuidedPlanningQuote;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
}

export function ProductDetailsHoverCard({
  quote,
  children,
  align = 'start',
}: ProductDetailsHoverCardProps) {
  const isMobile = useIsMobile();
  const content = <ProductDetailsHoverContent sections={getDetailsSections(quote)} />;

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          className="w-72"
          align={align}
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className="w-96"
        align={align}
      >
        {content}
      </HoverCardContent>
    </HoverCard>
  );
}

export interface QuoteCardProps {
  quote: GuidedPlanningQuote;
  onSelect: (quote: GuidedPlanningQuote) => void;
  onRemove: (quote: GuidedPlanningQuote) => void;
  isAlreadyAdded?: boolean;
  disabled?: boolean;
  showSelectionLimitTooltip?: boolean;
}

export function QuoteCard({
  quote,
  onSelect,
  onRemove,
  isAlreadyAdded = false,
  disabled = false,
  showSelectionLimitTooltip = false,
}: QuoteCardProps) {
  const { flags } = useFeatureFlags();
  const isMobile = useIsMobile();

  const carrierName = getCarrierName(quote);
  const premiumAmount = getPremiumAmount(quote);
  const paymentOption = getPaymentOption(quote);
  const projectedCoverage = getProjectedCoverage(quote);
  const partnerProjectedCoverage = getPartnerProjectedCoverage(quote);
  const totalProjectedCoverage = projectedCoverage + (partnerProjectedCoverage || 0);
  const formattedROI = getFormattedROI(quote);
  const creditRating = AM_BEST_CARRIER_RATINGS[carrierName];
  const logo = flags.nycLifeDemo
    ? `/logos/nyl-logo.png`
    : `/logos/${carrierName.toLowerCase()}.svg`;
  const isJoint = !isTraditionalLTCQuote(quote) && quote.policyType === 'Joint Life';

  return (
    <Card
      className={cn(
        'relative flex flex-col p-0 transition-all duration-200 hover:shadow-md',
        isAlreadyAdded && 'border-primary ring-primary/20 ring-2',
      )}
    >
      {isJoint && (
        <Badge
          variant="default"
          className="absolute right-6 top-3 text-xs"
        >
          Joint Policy
        </Badge>
      )}
      <CardContent className="flex flex-1 flex-col p-6">
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="border-primary/20 size-16 shrink-0 select-none rounded-lg border p-2.5">
              <img
                src={logo}
                alt={flags.nycLifeDemo ? 'New York Life' : carrierName}
                className="size-full object-contain"
              />
            </div>

            <div className="flex flex-1 flex-col gap-0.5">
              {creditRating && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger>
                      <div className="w-fit cursor-help text-sm font-medium text-sky-600">
                        Credit Rating&nbsp;{creditRating}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm rounded-lg border-0 bg-black/90 px-4 py-3.5 shadow-none">
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-bold text-white">
                          {AM_BEST_RATING_INFO[creditRating]?.title}
                        </p>
                        <p className="text-sm text-white/80">
                          {AM_BEST_RATING_INFO[creditRating]?.description}
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <h3 className="text-base font-semibold text-gray-950">
                {flags.nycLifeDemo ? (PRODUCTS as any)?.[carrierName] : quote.productName}
              </h3>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground text-sm">
                  {flags.nycLifeDemo ? 'New York Life' : carrierName}
                </p>
                <span className="text-accent">|</span>
                <HoverCard openDelay={100}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground cursor-help text-sm underline hover:text-gray-700"
                    >
                      Product Details
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent
                    className={isMobile ? 'w-72' : 'w-96'}
                    align="start"
                  >
                    <ProductDetailsHoverContent sections={getDetailsSections(quote)} />
                  </HoverCardContent>
                </HoverCard>
              </div>
            </div>
          </div>

          <div className="flex gap-6 border-t pt-4">
            {totalProjectedCoverage > 0 && (
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs font-medium">Projected Coverage</p>
                <p className="text-lg font-semibold leading-tight text-gray-950">
                  <LtcCostDisplay value={totalProjectedCoverage} />
                </p>
                {formattedROI && (
                  <span className="bg-primary/10 text-primary inline-block rounded px-1.5 py-0.5 text-xs font-medium">
                    {formattedROI} ROI
                  </span>
                )}
                {partnerProjectedCoverage !== undefined && (
                  <div className="flex gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground text-xs font-medium">You</div>
                      <div className="text-sm font-semibold leading-tight text-gray-950">
                        <LtcCostDisplay value={projectedCoverage} />
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-xs font-medium">Partner</div>
                      <div className="text-sm font-semibold leading-tight text-gray-950">
                        <LtcCostDisplay value={partnerProjectedCoverage} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1">
              <p className="text-muted-foreground text-xs font-medium">Cost</p>
              <p className="text-lg font-semibold text-gray-950">{formatCurrency(premiumAmount)}</p>
              {paymentOption && (
                <div className="mt-0.5 flex items-center gap-1">
                  <p className="text-muted-foreground text-xs italic">{paymentOption}</p>
                  {getPaymentOptionTooltip(paymentOption, premiumAmount) && (
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="text-muted-foreground size-3.5 shrink-0 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>{getPaymentOptionTooltip(paymentOption, premiumAmount)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">Underwriting Analysis</p>
              <UnderwritingStatus
                productName={quote.productName}
                fundingPolicyType={quote.fundingPolicyType}
                policyType={(quote as PolicyQuote).policyType}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          {showSelectionLimitTooltip ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger className="w-full">
                  <span className="flex-1">
                    <Button
                      variant="default"
                      className="w-full"
                      disabled
                    >
                      Model this in my plan
                      <ArrowRight />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>One policy at a time (more coming soon)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              variant={isAlreadyAdded ? 'outline' : 'default'}
              className="w-full flex-1"
              onClick={() => onSelect(quote)}
              disabled={disabled}
            >
              {isAlreadyAdded ? (
                <>
                  <Check />
                  Added to plan
                </>
              ) : (
                <>
                  Model this in my plan
                  <ArrowRight />
                </>
              )}
            </Button>
          )}

          {isAlreadyAdded && !disabled && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                >
                  <MoreVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => onRemove(quote)}
                >
                  Remove from plan
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
